/**
 * Discount Campaign entity class
 */

/**
 * DiscountCampaignFactory
 * returns Discount Campaign entity class
 *
 * @class DiscountCampaignFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.DiscountCampaign} class
 *
 * @ngdoc factory
 * @name mwApp.factory:DiscountCampaignFactory
 */
import { has } from 'app/helpers/object'
import { formatDateByTimezone, timeStringToDate } from '../../../app/helpers/date'
import moment from 'moment/moment'

/* @ngInject */
export default function DiscountCampaignFactory ($http, $injector, alertLiteSvc, Entity, FileUploadMixin, m2mMixin) {
  /* === DiscountCampaign class === */

  /**
   * DiscountCampaign model
   *
   * @class DiscountCampaign
   * @classdesc DiscountCampaign model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @return {models.DiscountCampaign} DiscountCampaign entity
   */
  function DiscountCampaign () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  DiscountCampaign.prototype = Object.create(Entity.prototype)
  DiscountCampaign.prototype.constructor = DiscountCampaign

  /* === DiscountCampaign instance methods & fields === */

  Object.assign(DiscountCampaign.prototype, {

    /**
     * instance fields
     *
     * @memberof models.DiscountCampaign#
     * @type {Object}
     */
    _fields: {
      id: Number,
      applyFrom: Number,
      applyTo: Number,
      applyToRecurringPayments: Boolean,
      code: String,
      currency: 'USD',
      description: String,
      endTime: String,
      expirationTime: String,
      name: String,
      networkId: String,
      startNow: Boolean,
      startTime: String,
      status: String,
      subscriptions: [{
        id: Number,
        autoPay: Boolean,
        currency: String,
        deleted: Boolean,
        enabled: Boolean,
        free: Boolean,
        linkId: Number,
        name: String
      }],
      value: Number,
      updatedAt: String,
      updatedBy: String,
      used: Boolean,
      $cachedApplyFrom: 1,
      $cachedApplyTo: 1,
      $cachedSubscriptionIds: [Number],
      $startDate: Number,
      $endDate: Number,
      $startTime: Number,
      $endTime: Number,
      $unlimitedApply: Boolean,
      $unlimitedPeriod: Boolean
    },

    /**
     * url template
     *
     * @memberof models.DiscountCampaign#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/discount-campaigns/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.DiscountCampaign#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/discount-campaigns/{{::id}}',
    /**
     * Prepare vod
     *
     * @memberof models.LiveEvent#
     * @override
     */
    _prepare: function () {
      this.$startDate = this.startTime ? formatDateByTimezone(this.startTime, false).toDate() : moment().toDate()
      this.$endDate = this.endTime ? formatDateByTimezone(this.endTime, false).toDate() : this.$startDate
      this.$startTime = timeStringToDate(this.startTime ? moment.utc(this.startTime).format('HH:mm:ss') : null)
      this.$endTime = timeStringToDate(this.endTime ? moment.utc(this.endTime).format('HH:mm:ss') : null)
      this.$unlimitedPeriod = this.id && !this.endTime
      this.$unlimitedApply = this.id && !this.applyTo
      this.$cachedSubscriptionIds = this.subscriptions.map(s => s.id)
      if (!this.applyToRecurringPayments) {
        this.applyTo = null
        this.applyFrom = null
      }
      if (this.status === 'ACTIVE') {
        this.$cachedApplyTo = this.applyTo
        this.$cachedApplyFrom = this.applyFrom
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.DiscountCampaign#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (this.startNow) {
        this.startTime = null
      } else {
        const startDate = this.$startDate.setHours(this.$startTime.getHours(), this.$startTime.getMinutes(), this.$startTime.getSeconds())
        this.startTime = formatDateByTimezone(startDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      }
      if (this.$unlimitedPeriod) {
        this.endTime = null
      } else {
        const endDate = this.$unlimitedPeriod ? null : this.$endDate.setHours(this.$endTime.getHours(), this.$endTime.getMinutes(), this.$endTime.getSeconds())
        this.endTime = this.$unlimitedPeriod ? null : formatDateByTimezone(endDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      }
      this.applyTo = this.$unlimitedApply ? null : this.applyTo
      return Entity.prototype._object.apply(this, arguments)
    }
  }, m2mMixin('subscriptions'), FileUploadMixin)

  /* === DiscountCampaign static methods & fields === */

  DiscountCampaign.getExtra = function (networkId) {
    return $http.get(`/nora/api/networks/${networkId}/discount-campaigns/extra/subscriptions`)
      .then(
        (response) => {
          return has('payload')(response.data)
            ? response.data.payload
            : response.data
        }
      ).catch(alertLiteSvc.alertError)
  }

  DiscountCampaign.checkingUniqCode = function (networkId, code) {
    return $http.get(`/nora/api/networks/${networkId}/discount-campaigns/extra/usage`, { params: { code } })
      .then(
        (response) => {
          return has('payload')(response.data)
            ? response.data.payload
            : response.data
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(DiscountCampaign, Entity)
  DiscountCampaign._name = 'DiscountCampaign'
  DiscountCampaign.init()

  // permissions
  DiscountCampaign.access = {
  }

  // extra
  DiscountCampaign.extra = {
  }
  return DiscountCampaign
}
